import React from 'react'
import EventLinks from '../EventLinks'
import EventTitle from '../EventTitle'
import { NarrowPageLayout } from '../PageLayout'
import ListingFaq from '../ListingFaq'
import { Block, BlockHeader } from '~/components/Block'
import Available from '../LegacyEventListings/Available'
import Purchaser from '../LegacyPurchaser'
import Menubar from '../Menubar'
import { MenubarContainer } from '../Menubar'
import EventMeta from '../EventMeta'
import { Cover, H2, fontWeight, space } from '@ticketswap/solar'
import styled from '@emotion/styled'
import { decodeGlobalId } from '~/utils/id'
import { withTranslation } from '~/i18n'
import { useViewer } from '~/components/ViewerContext'

const StyledH2 = styled(H2)`
  font-weight: ${fontWeight.regular};
`

const StyledCoverContent = styled(NarrowPageLayout)`
  padding-bottom: ${space[16]};
`

function ListingPageContent({ t, id, hash, randomCover, data }) {
  const { viewer = {}, listing = {} } = data
  const { event = {}, eventType = {} } = listing

  const { deviceInfo } = useViewer()
  const { isMobile } = deviceInfo

  const eventIsExpired =
    event.__typename === 'ExpiredEvent' || event.__typename === 'CancelledEvent'
  const eventTypeIsExpired = eventType && eventType.isExpired
  const listingIsSold = listing.status === 'SOLD'
  const listingIsDeleted = listing.status === 'DELETED'
  let ticketsFromListingInUsersCart = []
  if (viewer.cart && viewer.cart.tickets) {
    for (let i = 0; i < listing.tickets.edges.length; i++) {
      const ticketInListing = listing.tickets.edges[i].node
      for (let j = 0; j < viewer.cart.tickets.length; j++) {
        const ticketInCart = viewer.cart.tickets[j]
        if (ticketInListing.id === ticketInCart.id) {
          ticketsFromListingInUsersCart = [...ticketsFromListingInUsersCart, ticketInCart.id]
        }
      }
    }
  }

  const listingIsReserved =
    ticketsFromListingInUsersCart.length === 0 &&
    listing.numberOfTicketsStillForSale > 0 &&
    listing.tickets &&
    listing.tickets.edges &&
    listing.tickets.edges.filter(({ node }) => node.status === 'RESERVED').length ===
      listing.numberOfTicketsStillForSale
  const listingHasAvailableTickets = [
    eventIsExpired,
    eventTypeIsExpired,
    listingIsSold,
    listingIsDeleted,
    listingIsReserved,
  ].every(item => !item)

  const status = {
    eventIsExpired,
    eventTypeIsExpired,
    listingIsSold,
    listingIsDeleted,
    listingIsReserved,
    listingHasAvailableTickets,
  }
  const showOtherAvailableListings =
    !listingHasAvailableTickets && eventType && eventType.availableTicketsCount > 0

  const hasEventTypes = Boolean(event?.types?.edges.length > 1 && eventType)

  // Social links used in the Cover component
  const facebookUrl = event.facebookEventWalls?.filter(wall => wall.isMainEventWall)[0]
  const instagramUrl =
    event.instagramUsername && `https://www.instagram.com/${event.instagramUsername}/`

  const getPreviousPageLink = () => {
    const eventId = decodeGlobalId(event.id)
    const eventTypeId = eventType ? decodeGlobalId(eventType.id) : null
    const backRoute = hasEventTypes ? 'EVENT_TYPE_PAGE' : 'EVENT_PAGE'

    const params = {
      eventSlug: event.slug,
      eventTypeSlug: eventType && eventType.slug,
      eventId,
      eventTypeId,
    }

    return { backRoute, params }
  }

  const mobileCoverImage = isMobile ? event.imageUrl : null

  return (
    <>
      <Cover
        imageUrl={
          mobileCoverImage ||
          event.headerImageUrl ||
          (event.location && event.location.background) ||
          randomCover
        }
      >
        <MenubarContainer>
          <Menubar appearance="light" />
        </MenubarContainer>
        <StyledCoverContent>
          <EventLinks
            eventId={event.id}
            eventSlug={event.slug}
            getPreviousPageLink={getPreviousPageLink()}
            backBtnText={t('listing:header.back')}
            facebookUrl={facebookUrl}
            instagramUrl={instagramUrl}
            t={t}
          />
          <EventTitle eventId={event.id} eventName={event.name} eventSlug={event.slug} />
          {hasEventTypes && <StyledH2>{eventType.title}</StyledH2>}
          <EventMeta
            t={t}
            startDate={hasEventTypes ? eventType.startDate : event.startDate}
            endDate={hasEventTypes ? eventType.endDate : event.endDate}
            location={event.location}
          />
        </StyledCoverContent>
      </Cover>

      <NarrowPageLayout>
        <Purchaser
          t={t}
          status={status}
          listing={{ id, hash, ...listing }}
          ticketsFromListingInUsersCart={ticketsFromListingInUsersCart}
        />

        {showOtherAvailableListings && (
          <Block>
            <BlockHeader>
              <H2>{t('listing:listings.available')}</H2>
            </BlockHeader>
            <Available t={t} eventTypeId={eventType && eventType.id} />
          </Block>
        )}

        <ListingFaq t={t} showSecureSwapFaq={listing && listing.isSecureSwapAvailable} />
      </NarrowPageLayout>
    </>
  )
}

export default withTranslation(['common', 'listing', 'event-eventtype'])(ListingPageContent)
